import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {createRootRoute, createRoute, createRouter, Outlet} from "@tanstack/react-router";
import {lazy} from "react";
import {CartContextProvider} from "@/components/Providers/CartProvider.tsx";
import FeeProvider from "@/components/Providers/FeesProvider.tsx";
import InstructorFeeProposalsProvider from "@/components/Providers/InstructorFeeProposalsProvider.tsx";
import InstructorProgramsProvider from "@/components/Providers/InstructorProgramsProvider.tsx";
import JWTProvider, {JWTInstructorProvider} from "@/components/Providers/JWTProvider.tsx";
import {LoggedOutFeeProvider} from "@/components/Providers/LoggedOutFeeProvider.tsx";
import {MembershipReportAccessProvider} from "@/components/Providers/MembershipReportAccessProvider.tsx";
import ProgramsProvider from "@/components/Providers/ProgramsProvider.tsx";
import {StripeTerminalProvider} from "@/components/Providers/StripeTerminalProvider.tsx";
import CartHistoryPage from "@/pages/CartHistoryPage.tsx";
import CartPage from "@/pages/CartPage.tsx";
import CartReceipt from "@/pages/CartReceipt.tsx";
import EditProfile from "@/pages/EditProfile.tsx";
import FeeShortLink from "@/pages/FeeShortLink.tsx";
import HomePage from "@/pages/HomePage.tsx";
import Instructions from "@/pages/Instructions.tsx";
import ActivityDetailView from "@/pages/instructor/ActivityDetailView.tsx";
import FeeProposalsPage from "@/pages/instructor/FeeProposalsPage.tsx";
import NotFound from "@/pages/NotFound.tsx";
import PaymentComplete from "@/pages/PaymentComplete.tsx";
import RegisterGuest from "@/pages/RegisterGuest.tsx";
import SignIn from "@/pages/SignIn.tsx";
import SignOut from "@/pages/SignOut.tsx";
import TaxStatementPage from "@/pages/TaxStatementPage.tsx";
import RegisterCommunityMember from "./pages/RegisterCommunityMember";

const MyFeeDetailsPage = lazy(() => import("@/pages/instructor/MyFeeDetailsPage.tsx"));
const BoosterReportsPage = lazy(() => import("@/pages/instructor/BoosterReportsPage.tsx"));
const FeePage = lazy(() => import("@/pages/FeePage.tsx"));
const Programs = lazy(() => import("@/pages/Programs.tsx"));
const MyRegistrations = lazy(() => import("@/pages/MyRegistrations.tsx"));
const InstructorProgram = lazy(() => import("@/pages/instructor/InstructorProgram.tsx"));
const InstructorProgramList = lazy(() => import("@/pages/instructor/InstructorProgramList.tsx"));
const InstructorSignIn = lazy(() => import("@/pages/instructor/InstructorSignIn.tsx"));

const rootRoute = createRootRoute({
    component: () => (
        <LoggedOutFeeProvider>
            <Outlet />
        </LoggedOutFeeProvider>
    ),
});

const registerCommunityMemberRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/register-community-member",
    component: RegisterCommunityMember,
});
const registerGuestRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/register-guest",
    component: RegisterGuest,
});
const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: HomePage,
});
const feeShortLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/f/$code",
    component: FeeShortLink,
});

/**
 * Instructor Routes
 */
const instructorRoutes = createRoute({
    getParentRoute: () => rootRoute,
    path: "/instructor",
    component: () => (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <JWTInstructorProvider>
                <Outlet />
            </JWTInstructorProvider>
        </LocalizationProvider>
    ),
});

const instructorSignInRoute = createRoute({
    getParentRoute: () => instructorRoutes,
    path: "/sign-in",
    component: InstructorSignIn,
});

const instructorGuardRoutes = createRoute({
    getParentRoute: () => instructorRoutes,
    id: "instructor-guard",
    component: () => (
        <MembershipReportAccessProvider>
            <InstructorProgramsProvider>
                <StripeTerminalProvider debugEnabled={true}>
                    <InstructorFeeProposalsProvider>
                        <FeeProvider>
                            <CartContextProvider>
                                <Outlet />
                            </CartContextProvider>
                        </FeeProvider>
                    </InstructorFeeProposalsProvider>
                </StripeTerminalProvider>
            </InstructorProgramsProvider>
        </MembershipReportAccessProvider>
    ),
});

const instructorProgramListRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/",
    component: InstructorProgramList,
});

const instructorProgramRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/program/$programId",
    component: InstructorProgram,
});

const instructorActivityDetailRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/course/$programId/$courseId",
    component: ActivityDetailView,
});

const instructorFeeProposalsRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/fee-proposals",
    component: FeeProposalsPage,
});

const instructorMyFeeDetailRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/my-fee/$feeId",
    component: MyFeeDetailsPage,
});

const instructorBoosterReportsRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/boosters",
    component: BoosterReportsPage,
});

const instructorCartRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/cart",
    component: CartPage,
});

const instructorFeesRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/fees",
    component: FeePage,
});

const instructorPaymentCompleteRoute = createRoute({
    getParentRoute: () => instructorGuardRoutes,
    path: "/payment-complete",
    component: PaymentComplete,
});

/**
 * User Routes
 */
const userRoutes = createRoute({
    getParentRoute: () => rootRoute,
    id: "user-routes",
    component: () => (
        <JWTProvider>
            <StripeTerminalProvider debugEnabled={true}>
                <Outlet />
            </StripeTerminalProvider>
        </JWTProvider>
    ),
});

const userSignInRoute = createRoute({
    getParentRoute: () => userRoutes,
    path: "/signin",
    component: SignIn,
});

const userGuardRoutes = createRoute({
    getParentRoute: () => userRoutes,
    id: "user-guard",
    component: () => (
        <MembershipReportAccessProvider>
            <ProgramsProvider>
                <FeeProvider>
                    <CartContextProvider>
                        <Outlet />
                    </CartContextProvider>
                </FeeProvider>
            </ProgramsProvider>
        </MembershipReportAccessProvider>
    ),
});

const programsRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/programs",
    component: Programs,
});

const editProfileRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/edit-profile",
    component: EditProfile,
});

const myRegistrationsRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/my-registrations",
    component: MyRegistrations,
});

const cartRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/cart",
    component: CartPage,
});

const cartReceiptRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/cart-receipt/$cartId",
    component: CartReceipt,
});

const cartHistoryRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/cart-history",
    component: CartHistoryPage,
});

const feesRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/fees",
    component: FeePage,
});

const paymentCompleteRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/payment-complete",
    component: PaymentComplete,
});

const instructionsRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/instructions",
    component: Instructions,
});

const signOutRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/signout",
    component: SignOut,
});

const taxStatementRoute = createRoute({
    getParentRoute: () => userGuardRoutes,
    path: "/tax-statement",
    component: TaxStatementPage,
});

const routeTree = rootRoute.addChildren([
    indexRoute,
    feeShortLinkRoute,
    registerCommunityMemberRoute,
    registerGuestRoute,
    instructorRoutes.addChildren([
        instructorSignInRoute,
        instructorGuardRoutes.addChildren([
            instructorProgramListRoute,
            instructorProgramRoute,
            instructorActivityDetailRoute,
            instructorFeeProposalsRoute,
            instructorMyFeeDetailRoute,
            instructorBoosterReportsRoute,
            instructorCartRoute,
            instructorFeesRoute,
            instructorPaymentCompleteRoute,
        ]),
    ]),
    userRoutes.addChildren([
        userSignInRoute,
        userGuardRoutes.addChildren([
            programsRoute,
            editProfileRoute,
            myRegistrationsRoute,
            cartRoute,
            feesRoute,
            paymentCompleteRoute,
            instructionsRoute,
            signOutRoute,
            cartReceiptRoute,
            cartHistoryRoute,
            taxStatementRoute,
        ]),
    ]),
]);

export const appRouter = createRouter({
    routeTree: routeTree,
    defaultNotFoundComponent: NotFound,
});
